import {
  AI_MEASURE_TONES,
  resolveAiMeasureTone,
  type AiMeasureTone,
  type OrganizationCategory,
} from '@safetyhub/shared';

/**
 * AI 개선대책 문체 프리셋 — 설정 화면(`/settings/organization` 「AI 작성 문체」)이 그리는 라벨·설명·예문.
 * (결정 기록 `docs/decisions/2026-09-20-ai-measure-tone-preset.md` §7)
 *
 * 프리셋 값·파생 규칙은 shared(`AI_MEASURE_TONES`·`resolveAiMeasureTone`)가 정본이고 이 파일은 표시 문구만
 * 소유한다 — 예문은 **같은 문장**을 두 문체로 적어 차이가 문체뿐임을 보이게 한다.
 *
 * ⚠️ 왜 `lib/organizations`가 아니라 별도 모듈인가: 그 모듈은 web spec 20여 곳이 `vi.mock`으로 통째 대체한다
 * (API 클라이언트라서). 순수 함수·상수를 거기 두면 mock 팩토리가 이 export를 모르는 spec이 한꺼번에 깨진다
 * (`lib/memberStatus.ts`와 같은 이유).
 */
export interface AiMeasureTonePreset {
  value: AiMeasureTone;
  label: string;
  /** 어떤 문서에 맞는 문체인지 한 줄 */
  description: string;
  /** 같은 개선대책을 이 문체로 적은 예문 */
  example: string;
}

const PRESET_COPY: Record<AiMeasureTone, Omit<AiMeasureTonePreset, 'value'>> = {
  FORMAL: {
    label: '공문체',
    description: '자체 점검 기록용',
    example: '작업발판 전 구간에 상부 및 중간 난간대를 기준에 맞게 설치해야 함',
  },
  ADVISORY: {
    label: '권고체',
    description: '고객사·현장에 전달하는 지도문서용',
    example: '작업발판 전 구간에 상부 및 중간 난간대를 기준에 맞게 설치하시기 바랍니다.',
  },
};

/** 화면이 map할 프리셋 목록 — shared 상수 순서 그대로 파생(손나열 금지, 프리셋이 늘면 여기서 저절로 는다). */
export const AI_MEASURE_TONE_PRESETS: readonly AiMeasureTonePreset[] = AI_MEASURE_TONES.map((value) => ({
  value,
  ...PRESET_COPY[value],
}));

/**
 * 사용자가 고른 카드를 저장값으로 옮긴다.
 *
 * 저장값 `null` = 「조직 유형에서 파생」. 파생값과 같은 카드를 고르면 명시값을 남기지 않고 `null`로 저장해
 * 조직 유형이 바뀌면 문체도 따라가게 하고, 다른 카드를 고르면 그 값을 저장한다.
 */
export function toStoredAiMeasureTone(
  selected: AiMeasureTone,
  category: OrganizationCategory,
): AiMeasureTone | null {
  return selected === resolveAiMeasureTone(null, category) ? null : selected;
}
